import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import DatePicker from '@/components/ui/DatePicker'
import Pagination from '@/components/ui/Pagination'
import TitleRow from '@/components/ui/TitleRow'
import {
  FLOORSHEET_RANGE_AVAILABLE,
  type FloorsheetDay,
} from '@/features/floorsheet/api/floorsheetService'
import {
  useFloorsheetBrokerOptions,
  useFloorsheetInsights,
  useFloorsheetSummary,
  useFloorsheetTrades,
} from '@/features/floorsheet/api/queries'
import FloorsheetFilters from '@/features/floorsheet/components/FloorsheetFilters'
import FloorsheetSummaryCard from '@/features/floorsheet/components/FloorsheetSummaryCard'
import FloorsheetTradeTable from '@/features/floorsheet/components/FloorsheetTradeTable'
import WhereTheMoneyWent from '@/features/floorsheet/components/WhereTheMoneyWent'
import { formatBsDate } from '@/lib/bsDate'
import { FloorsheetBoardSkeleton } from '@/screens/market/components/FloorsheetBoardSkeleton'
import {
  floorsheetDayOf,
  floorsheetSearchSchema,
  loadFloorsheet,
} from '@/screens/market/FloorsheetScreen.loader'
import type { FloorsheetQuery } from '@/schemas/floorsheet'
import { pageSizeSchema } from '@/schemas/table'

/** Known without data, so the pending page draws the real one. */
const HEADER = <TitleRow title="Floorsheet" />

function FloorsheetPending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading the floorsheet…"
      header={HEADER}
      pageTitle="Floorsheet"
    >
      <FloorsheetBoardSkeleton />
    </ScreenLayout>
  )
}

/** Kept light — RouteErrorPage plus the same header the pending page shows — so the frame does
 *  not change between the two: `errorComponent`s ship in the entry chunk. */
function FloorsheetError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} />
}

export const Route = createFileRoute('/_app/market/floorsheet')({
  validateSearch: floorsheetSearchSchema,
  loader: loadFloorsheet,
  pendingComponent: FloorsheetPending,
  errorComponent: FloorsheetError,
  component: FloorsheetScreen,
})

/** The title over the stat panel, for the day the page shows. */
const titleFor = (day: FloorsheetDay) =>
  day.kind === 'today'
    ? 'Today across NEPSE'
    : day.kind === 'day'
      ? `${formatBsDate(day.date)} across NEPSE`
      : `${formatBsDate(day.from)} to ${formatBsDate(day.to)}`

/**
 * Market · Floorsheet (mock `#/market/floorsheet`): one day's executed trades.
 *
 *   title row       "Today across NEPSE" (a glossary term) · the date button and its popover
 *   stat panel      trades, kitta, value, companies, and Mitra's stamp
 *   filters         Symbol · Buyer · Seller
 *   trade log       server-paged, sortable by amount and time, then the pager
 *   two cards       busiest brokers and most traded shares, today
 *
 * One trade log, not three groupings: the By Broker and By Symbol tabs are gone (owner ruling 7).
 */
function FloorsheetScreen() {
  const search = Route.useSearch()
  const { page, pageSize, symbol, buyer, seller, broker, sortBy, sortDesc } = search
  const navigate = Route.useNavigate()
  const day = floorsheetDayOf(search)

  const query: FloorsheetQuery = { page, pageSize, symbol, buyer, seller, broker, sortBy, sortDesc }

  /**
   * A 300ms debounce between the Symbol field and `?symbol=`, as Events' search box does, so
   * typing "NABIL" is one request, not five.
   */
  const [symbolTerm, setSymbolTerm] = useState(symbol ?? '')
  const lastCommittedSymbol = useRef(symbol)
  useEffect(() => {
    const next = symbolTerm.trim().toUpperCase() || undefined
    if (next === lastCommittedSymbol.current) return
    const id = setTimeout(() => {
      lastCommittedSymbol.current = next
      void navigate({
        search: (prev) => ({
          ...prev,
          symbol: next,
          // No endpoint serves an either-side broker AND a symbol together, so leaving the chip
          // on would claim a narrowing the log cannot perform.
          ...(next === undefined ? {} : { broker: undefined }),
          page: 1,
        }),
      })
    }, 300)
    return () => {
      clearTimeout(id)
    }
  }, [symbolTerm, navigate])

  const summary = useFloorsheetSummary(day)
  const trades = useFloorsheetTrades(query, day)
  const insights = useFloorsheetInsights(day)
  const brokers = useFloorsheetBrokerOptions()

  const changeDay = (next: FloorsheetDay) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        date: next.kind === 'day' ? next.date : undefined,
        from: next.kind === 'range' ? next.from : undefined,
        to: next.kind === 'range' ? next.to : undefined,
        page: 1,
      }),
    })
  }

  return (
    <ScreenLayout header={HEADER} pageTitle="Floorsheet">
      <FloorsheetSummaryCard
        summary={summary.data}
        isLoading={summary.isLoading}
        title={titleFor(day)}
        dateSlot={
          <DatePicker
            value={day}
            onChange={changeDay}
            cta="Show trades"
            rangeAvailable={FLOORSHEET_RANGE_AVAILABLE}
          />
        }
      />

      <FloorsheetFilters
        symbolTerm={symbolTerm}
        onSymbolTermChange={setSymbolTerm}
        buyer={buyer}
        seller={seller}
        onSideChange={(side, code) => {
          void navigate({
            search: (prev) => ({
              ...prev,
              [side]: code,
              // An either-side `broker` from an inbound link would keep narrowing under the two
              // fields a member can see; the first pick replaces it.
              broker: undefined,
              page: 1,
            }),
          })
        }}
        broker={broker}
        brokerIgnored={broker !== undefined && symbol !== undefined}
        onClearBroker={() => {
          void navigate({ search: (prev) => ({ ...prev, broker: undefined, page: 1 }) })
        }}
        brokers={brokers.data ?? []}
      />

      {/* The table and its pager as one block: `Pagination` draws its own 24 above. */}
      <div>
        <FloorsheetTradeTable
          rows={trades.data?.rows ?? []}
          isLoading={trades.isLoading}
          isError={trades.isError}
          onRetry={() => {
            void trades.refetch()
          }}
          retrying={trades.isFetching}
          pageSize={pageSize}
          market={{
            sortBy,
            sortDesc,
            onSortChange: (nextBy, nextDesc) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  sortBy: nextBy,
                  sortDesc: nextBy === undefined ? undefined : nextDesc,
                  page: 1,
                }),
              })
            },
          }}
        />
        <Pagination
          page={page}
          pageSize={pageSize}
          total={trades.data?.total ?? 0}
          unit="trades"
          onPageChange={(next) => {
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }}
          onPageSizeChange={(next) => {
            void navigate({
              search: (prev) => ({ ...prev, pageSize: pageSizeSchema.parse(next), page: 1 }),
            })
          }}
        />
      </div>

      <WhereTheMoneyWent
        day={day}
        insights={insights}
        onPickSymbol={(picked) => {
          setSymbolTerm(picked)
        }}
      />
    </ScreenLayout>
  )
}
